import * as H from "harmaja"
import { componentScope, h, ListView } from "harmaja"
import * as L from "lonna"
import {
    Board,
    canWrite,
    findConnection,
    findItem,
    getConnection,
    Id,
    Image,
    Item,
    newNote,
    Note,
    Video,
    Reaction,
    Embed,
    Drawing,
    Table,
} from "../../../common/src/domain"
import { isFirefox } from "../components/browser"
import { ModalContainer } from "../components/ModalContainer"
import { isEmbedded } from "../embedding"
import { AssetStore } from "../store/asset-store"
import { BoardState, BoardStore, Dispatch } from "../store/board-store"
import { CursorsStore } from "../store/cursors-store"
import { UserSessionState } from "../store/user-session-store"
import { boardCoordinateHelper } from "./board-coordinates"
import { boardDragHandler } from "./board-drag"
import {
    BoardFocus,
    getSelectedItemIds,
    getSelectedItem,
    getSelectedItems,
    noFocus,
    getSelectedConnectionIds,
} from "./board-focus"
import { boardScrollAndZoomHandler } from "./board-scroll-and-zoom"
import { BoardToolLayer } from "./toolbars/BoardToolLayer"
import { ConnectionsView } from "./ConnectionsView"
import { ContextMenuView } from "./contextmenu/ContextMenuView"
import { CursorsView } from "./CursorsView"
import { ReactionsView, REACTION_EMOJIS } from "./ReactionsView"
import * as G from "../../../common/src/geometry"
import { imageUploadHandler, imageDropHandler } from "./image-upload"
import { ImageView } from "./ImageView"
import { itemCreateHandler } from "./item-create"
import { cutCopyPasteHandler } from "./item-cut-copy-paste"
import { itemDeleteHandler } from "./item-delete"
import { itemDuplicateHandler } from "./item-duplicate"
import { itemMoveWithArrowKeysHandler } from "./item-move-with-arrow-keys"
import { itemSelectAllHandler } from "./item-select-all"
import { withCurrentContainer } from "./item-setcontainer"
import { itemUndoHandler } from "./item-undo-redo"
import { ItemView } from "./ItemView"
import { installKeyboardShortcut, plainKey } from "./keyboard-shortcuts"
import { RectangularDragSelection } from "./RectangularDragSelection"
import { SelectionBorder } from "./SelectionBorder"
import { synchronizeFocusWithServer } from "./synchronize-focus-with-server"
import { ToolController } from "./tool-selection"
import { BoardViewHeader } from "./header/BoardViewHeader"
import { VideoView } from "./VideoView"
import { EmbedView } from "./EmbedView"
import { DrawingView } from "./DrawingView"
import { TableView } from "./TableView"
import { itemDrawHandler } from "./item-draw"
import { startConnecting } from "./item-connect"
import { emptySet } from "../../../common/src/sets"
import { installZoomKeyboardShortcuts } from "./zoom-shortcuts"
import { itemZOrderHandler } from "./item-z-order"
import { itemVoteHandler } from "./item-vote"
import { frameNavigator } from "./frames"
import { itemMindMapHandler } from "./item-mindmap"
import { itemGroupHandler } from "./item-group"
import { RightClickMenu } from "./RightClickMenu"
import { GroupOutlines } from "./GroupOutlines"
import { SearchView } from "./SearchView"
import { PresentationHud } from "./PresentationHud"
import { exportBoardPng } from "./export-image"
import { boundingRect } from "./boardContentArea"
import { itemHideContentsHandler } from "./item-hide-contents"

const emptyNote = newNote("")

export const BoardView = ({
    boardId,
    cursors,
    boardStore,
    sessionState,
    assets,
    dispatch,
}: {
    boardId: string
    cursors: CursorsStore
    boardStore: BoardStore
    sessionState: L.Property<UserSessionState>
    assets: AssetStore
    dispatch: Dispatch
}) => {
    const boardState = boardStore.state
    const board = boardState.pipe(
        L.map((s: BoardState) => s.board!),
        L.filter((b: Board) => !!b, componentScope()),
    )
    const accessLevel = L.view(boardState, "accessLevel")
    const locks = L.view(boardState, (s) => s.locks)
    const sessionId = L.view(sessionState, (s) => s.sessionId)
    const sessions = L.view(boardState, (s) => s.users)
    const zoom = L.atom({ zoom: 1, quickZoom: 1 })

    const containerElement = L.atom<HTMLElement | null>(null)
    const scrollElement = L.atom<HTMLElement | null>(null)
    const boardElement = L.atom<HTMLElement | null>(null)
    const latestNoteId = L.atom<Id | null>(null)
    const latestNote = L.view(latestNoteId, board, (id, b) => {
        const note = id ? findItem(b)(id) : null
        return (note as Note) || emptyNote
    })
    const latestConnectionId = L.atom<Id | null>(null)
    const latestConnection = L.view(latestConnectionId, board, (id, b) => {
        return id ? findConnection(b)(id) : null
    })
    const focus = synchronizeFocusWithServer(board, locks, sessionId, dispatch)
    const coordinateHelper = boardCoordinateHelper(containerElement, scrollElement, boardElement, zoom)
    const toolController = ToolController()
    const tool = toolController.tool

    let previousFocus: BoardFocus | null = null
    focus.forEach((f) => {
        const previousIDs = previousFocus && getSelectedItemIds(previousFocus)
        const itemIds = [...getSelectedItemIds(f)].filter((id) => !previousIDs || !previousIDs.has(id))
        previousFocus = f
        if (itemIds.length > 0) {
            // Lock newly selected items for this session (shows as "selected by another user" elsewhere).
            // Selection no longer changes z-order; use ] / [ for that.
            itemIds.forEach((itemId) => dispatch({ action: "item.lock", boardId: board.get().id, itemId }))
            const item = getSelectedItem(board.get())(f)
            if (item && item.type === "note") {
                latestNoteId.set(item.id)
            }
        }
        const connectionId = [...getSelectedConnectionIds(f)][0]
        if (connectionId && getConnection(board.get())(connectionId)?.action === "connect") {
            latestConnectionId.set(connectionId)
        }
    })

    tool.pipe(L.changes).forEach((tool) => {
        if (!["note", "container", "text", "embed", "table"].includes(tool) && focus.get().status === "adding") {
            focus.set(noFocus)
        }
    })

    const doOnUnmount: Function[] = []

    const itemsList = L.view(L.view(board, "items"), Object.values)

    function onURL(assetId: string, url: string) {
        itemsList.get().forEach((i) => {
            if ((i.type === "image" || i.type === "video") && i.assetId === assetId && i.src != url) {
                dispatch({ action: "item.update", boardId, items: [{ id: i.id, src: url }] })
            }
        })
    }
    const uploadImageFile = imageUploadHandler(assets, coordinateHelper, onAdd, onURL)

    const clipboard = cutCopyPasteHandler(
        board,
        boardStore.crdtStore,
        focus,
        coordinateHelper,
        dispatch,
        uploadImageFile,
    )
    doOnUnmount.push(clipboard.remove)

    const zoomControls = boardScrollAndZoomHandler(
        board,
        boardElement,
        scrollElement,
        zoom,
        coordinateHelper,
        toolController,
    )
    const { viewRect } = zoomControls

    boardStore.eventsFromServer.forEach((e) => {
        if (e.action === "user.bringAllToMe") {
            console.log(`Following user ${e.nickname}`, e)
            viewRect.set(e.viewRect)
        }
    })

    // Emoji reactions: keys 1-6 drop one at the cursor, broadcast to others
    const reactions = L.bus<Reaction>()
    boardStore.eventsFromServer.forEach((e) => {
        if (e.action === "user.reaction") reactions.push(e)
    })
    REACTION_EMOJIS.forEach((emoji, i) =>
        installKeyboardShortcut(plainKey(String(i + 1)), () => {
            const r: Reaction = {
                action: "user.reaction",
                boardId,
                sessionId: sessionId.get()!,
                emoji,
                position: coordinateHelper.currentBoardCoordinates.get(),
            }
            reactions.push(r)
            dispatch(r)
        }),
    )

    // Export selection (or whole content) as PNG
    async function exportPng() {
        const b = board.get()
        const selected = getSelectedItems(b)(focus.get())
        const items = selected.length ? selected : Object.values(b.items).filter((i) => !i.hidden)
        const el = boardElement.get()
        if (!items.length || !el) return
        const r = boundingRect(items)
        const margin = Math.max(r.width, r.height) * 0.05
        const rect = { x: r.x - margin, y: r.y - margin, width: r.width + 2 * margin, height: r.height + 2 * margin }
        const previousFocus = focus.get()
        const previousView = viewRect.get()
        focus.set(noFocus) // no selection outlines in the image
        zoom.set({ zoom: 1, quickZoom: 1 }) // px-floored sizes (min 6px nodes etc) only look right near zoom 1
        try {
            await new Promise((resolve) => setTimeout(resolve, 100))
            await exportBoardPng(el, rect, coordinateHelper.emToBoardPx(1), `${b.name || "board"}.png`)
        } finally {
            viewRect.set(previousView)
            focus.set(previousFocus)
        }
    }
    installKeyboardShortcut((e) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "e", exportPng)

    // Deep link: /b/<board>#item=<id> selects and zooms to the item on load
    const linkedItem = location.hash.match(/#item=([\w-]+)/)?.[1]
    if (linkedItem) {
        let done = false
        board.forEach((b) => {
            if (done) return
            const item = b.items[linkedItem]
            if (!item) return
            done = true
            setTimeout(() => {
                focus.set({ status: "selected", itemIds: new Set([item.id]), connectionIds: emptySet() })
                zoomControls.zoomToFit([item])
            }, 100)
        })
    }

    const draw = itemDrawHandler(boardElement, board, coordinateHelper, toolController, focus, dispatch)

    imageDropHandler(boardElement, assets, focus, uploadImageFile)
    itemCreateHandler(board, focus, latestNote, boardElement, onAdd)
    itemDeleteHandler(boardId, dispatch, focus)
    itemDuplicateHandler(board, boardStore.crdtStore, dispatch, focus)
    itemHideContentsHandler(board, focus, dispatch)
    itemMoveWithArrowKeysHandler(board, dispatch, focus)
    itemUndoHandler(dispatch)
    itemZOrderHandler(board, focus, dispatch)
    itemVoteHandler(board, focus, dispatch)
    itemMindMapHandler(board, focus, dispatch)
    itemGroupHandler(board, focus, dispatch)
    itemSelectAllHandler(board, focus)
    installZoomKeyboardShortcuts(zoomControls, board, focus)
    const frames = frameNavigator(board, viewRect, zoomControls.zoomToFit)
    installKeyboardShortcut(
        (e) => e.key === "Escape",
        () => {
            toolController.useDefaultTool()
            focus.set(noFocus)
        },
    )
    installKeyboardShortcut(
        (e) => e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey,
        () => {
            const item = getSelectedItem(board.get())(focus.get())
            if (item && (item.type === "note" || item.type === "text"))
                focus.set({ status: "editing", itemId: item.id })
        },
    )
    installKeyboardShortcut(plainKey("c"), () => toolController.tool.set("connect"))
    installKeyboardShortcut(plainKey("l"), () => toolController.tool.set("line"))
    // Hold Space to pan, release to restore previous tool
    installKeyboardShortcut(
        (e) => e.key === " " && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement),
        (e) => {
            if (!e.repeat) toolController.setTemporaryTool("pan")
        },
        () => toolController.restoreTool(),
    )
    L.fromEvent(window, "blur")
        .pipe(L.applyScope(componentScope()))
        .forEach(() => toolController.restoreTool())
    // Selection is cleared only by clicking the empty canvas or pressing Esc (see onClick / Escape shortcut);
    // clicks on toolbars, header, minimap etc. keep it, like Miro/FigJam.

    function onClick(e: JSX.UIEvent) {
        const f = focus.get()
        if (f.status === "connection-adding") {
            toolController.useDefaultTool()
        } else if (f.status === "adding") {
            onAdd(f.item)
        } else {
            if (e.target === boardElement.get()) {
                if (tool.get() === "connect") {
                    startConnecting(
                        board,
                        coordinateHelper,
                        latestConnection,
                        dispatch,
                        toolController,
                        focus,
                        coordinateHelper.currentBoardCoordinates.get(),
                    )
                } else {
                    focus.set(noFocus)
                }
            }
        }
    }

    function onAdd(item: Item, at?: G.Coordinates) {
        toolController.useDefaultTool()
        const point = at ?? coordinateHelper.currentBoardCoordinates.get()
        const { x, y } = item.type !== "container" ? G.add(point, { x: -item.width / 2, y: -item.height / 2 }) : point
        item = withCurrentContainer({ ...item, x, y }, board.get())

        dispatch({ action: "item.add", boardId, items: [item], connections: [] })

        if ((item.type === "note" || item.type === "text") && item.text !== "") {
            // shapes are notes with empty text: select them instead of opening the editor
            focus.set({ status: "editing", itemId: item.id })
        } else {
            focus.set({ status: "selected", itemIds: new Set([item.id]), connectionIds: emptySet() })
        }
    }

    coordinateHelper.currentBoardCoordinates.pipe(L.throttle(30)).forEach((position) => {
        dispatch({ action: "cursor.move", position, boardId })
    })

    const { selectionRect } = boardDragHandler({
        ...{
            board,
            boardElem: boardElement,
            coordinateHelper,
            latestConnection,
            focus,
            toolController,
            dispatch,
        },
    })

    H.onUnmount(() => {
        doOnUnmount.forEach((fn) => fn())
    })

    const boardAccessStatus = L.view(boardState, (s) => s.status)
    const quickZoom = L.view(zoom, "quickZoom")
    const mainZoom = L.view(zoom, "zoom")
    const borderContainerStyle = L.combineTemplate({
        width: L.view(board, quickZoom, (b) => b.width + "em"),
        height: L.view(board, quickZoom, (b) => b.height + "em"),
        fontSize: L.view(mainZoom, (z) => z + "em"),
        transform: L.view(quickZoom, (z) => {
            const percentTranslate = ((z - 1) / 2) * 100
            return `translate(${percentTranslate}%, ${percentTranslate}%) scale(${z})`
        }),
        "will-change": "transform, fontSize",
    })

    // Dot grid: dot size is fixed in screen px (CSS), spacing doubles/halves so it stays ~40-80px on screen
    const dotGridStyle = L.view(zoom, (z) => {
        const pxPerEm = 16 * z.zoom * z.quickZoom // root font-size 16px, board em = zoom
        const base = 1.125 // em: 18px at zoom 1, per the design handoff
        const level = Math.ceil(Math.log2(18 / (base * pxPerEm)))
        const spacing = base * Math.pow(2, level)
        return { backgroundSize: `${spacing}em ${spacing}em`, backgroundPosition: `${spacing / 2}em ${spacing / 2}em` }
    })

    const drawPreviewStyle = L.view(board, (b) => ({
        position: "absolute",
        top: 0,
        left: 0,
        width: b.width + "em",
        height: b.height + "em",
        pointerEvents: "none",
        zIndex: 200000001,
        overflow: "visible",
    }))

    const className = L.view(
        boardAccessStatus,
        frames.presenting,
        (status, presenting) =>
            `board-container ${isEmbedded() ? "embedded" : ""} ${status} ${presenting ? "presenting" : ""}`,
    )

    const items = L.view(L.view(board, "items"), Object.values, (items) => items.filter((i) => !i.hidden))
    const selectedItems = L.view(board, focus, (b, f) => getSelectedItems(b)(f))
    const modalContent = L.atom<any>(null)

    L.interval(100, null, componentScope()).forEach(() => {
        if (window.scrollY !== 0 || window.scrollX !== 0) {
            if (focus.get().status !== "editing") {
                // Reset scroll position when not editing. At least iOS seems to need this. It's vital that our scroll pos stays at origin.
                window.scrollTo(0, 0)
            }
        }
    })
    return (
        <div id="root" className={className}>
            <ModalContainer content={modalContent} />
            <BoardViewHeader
                {...{
                    board,
                    usersOnBoard: L.view(boardState, "users"),
                    sessionState,
                    dispatch,
                    accessLevel,
                    modalContent,
                    eventsFromServer: boardStore.eventsFromServer,
                    viewRect,
                    online: L.view(boardStore.state, (s) => s.status === "online"),
                    boardState: boardStore.state,
                    crdtStore: boardStore.crdtStore,
                    frames,
                    exportPng,
                }}
            />
            <div className="content-container" ref={containerElement.set}>
                <div className="scroll-container" ref={scrollElement.set}>
                    <div className="border-container" style={borderContainerStyle}>
                        <div
                            className={L.view(tool, (t) => "board " + t)}
                            style={dotGridStyle}
                            draggable={isFirefox ? L.view(focus, (f) => f.status !== "editing") : true}
                            ref={boardElement.set}
                            onClick={onClick}
                            onTouchEnd={onClick}
                        >
                            <ListView observable={items} renderObservable={renderItem} getKey={(i) => i.id} />

                            {L.view(tool, (t) =>
                                t === "connect" ? null : (
                                    <ListView
                                        observable={selectedItems}
                                        renderObservable={renderSelectionBorder}
                                        getKey={(i) => i.id}
                                    />
                                ),
                            )}
                            <GroupOutlines board={board} focus={focus} boardElement={boardElement} />
                            <RectangularDragSelection {...{ rect: selectionRect }} />
                            <CursorsView {...{ cursors, sessions, viewRect }} />
                            <ReactionsView reactions={reactions} />
                            <svg className="draw-preview" style={drawPreviewStyle}>
                                <path
                                    d={draw.livePath}
                                    fill="none"
                                    stroke={L.view(draw.livePath, () => draw.penColor())}
                                    stroke-width={draw.strokeWidth}
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <ContextMenuView
                                {...{
                                    latestNote,
                                    crdtStore: boardStore.crdtStore,
                                    dispatch,
                                    board,
                                    focus,
                                    viewRect,
                                }}
                            />
                            <ConnectionsView {...{ board, zoom, dispatch, focus, coordinateHelper }} />
                        </div>
                    </div>
                </div>
                <SearchView board={board} focus={focus} zoomToFit={zoomControls.zoomToFit} />
                <RightClickMenu
                    {...{
                        board,
                        focus,
                        dispatch,
                        boardElement,
                        coordinateHelper,
                        crdtStore: boardStore.crdtStore,
                        latestNote,
                        onAdd,
                        zoomToFit: zoomControls.zoomToFit,
                        exportPng,
                        pasteFromMirror: clipboard.pasteFromMirror,
                        hasMirror: clipboard.hasMirror,
                        selectAll: () =>
                            focus.set({
                                status: "selected",
                                itemIds: new Set(Object.keys(board.get().items)),
                                connectionIds: new Set(board.get().connections.map((c) => c.id)),
                            }),
                    }}
                />
                <PresentationHud {...frames} />
                <BoardToolLayer
                    {...{
                        board,
                        accessLevel,
                        boardStore,
                        containerElement,
                        coordinateHelper,
                        dispatch,
                        focus,
                        latestNote,
                        onAdd,
                        toolController,
                        sessionState,
                        ...zoomControls,
                    }}
                />
            </div>
        </div>
    )

    function renderSelectionBorder(id: string, item: L.Property<Item>) {
        return <SelectionBorder {...{ id, tool, item: item, coordinateHelper, board, focus, dispatch }} />
    }

    function renderItem(id: string, item: L.Property<Item>) {
        const isLocked = L.combineTemplate({ locks, sessionId }).pipe(
            L.map(({ locks, sessionId }) => !!locks[id] && locks[id] !== sessionId),
        )

        return L.view(L.view(item, "type"), (t) => {
            switch (t) {
                case "container":
                case "text":
                case "note":
                    return (
                        <ItemView
                            {...{
                                board,
                                history,
                                id,
                                type: t,
                                item: item as L.Property<Note>,
                                isLocked,
                                focus,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                                toolController,
                                accessLevel,
                                boardStore,
                            }}
                        />
                    )
                case "image":
                    return (
                        <ImageView
                            {...{
                                id,
                                image: item as L.Property<Image>,
                                assets,
                                board,
                                isLocked,
                                focus,
                                toolController,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                            }}
                        />
                    )
                case "video":
                    return (
                        <VideoView
                            {...{
                                id,
                                video: item as L.Property<Video>,
                                assets,
                                board,
                                isLocked,
                                focus,
                                toolController,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                            }}
                        />
                    )
                case "drawing":
                    return (
                        <DrawingView
                            {...{
                                id,
                                drawing: item as L.Property<Drawing>,
                                board,
                                isLocked,
                                focus,
                                toolController,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                            }}
                        />
                    )
                case "table":
                    return (
                        <TableView
                            {...{
                                id,
                                table: item as L.Property<Table>,
                                board,
                                isLocked,
                                focus,
                                toolController,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                            }}
                        />
                    )
                case "embed":
                    return (
                        <EmbedView
                            {...{
                                id,
                                embed: item as L.Property<Embed>,
                                board,
                                isLocked,
                                focus,
                                toolController,
                                coordinateHelper,
                                latestConnection,
                                dispatch,
                            }}
                        />
                    )
                default:
                    throw Error("Unsupported item: " + t)
            }
        })
    }
}
